import { useFormik } from 'formik';
import { AgButton, AgInput, AgModal, AgModalActions, AgModalContent } from 'src/components/ui';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';

type AddCommodityAttributeModalProps = {
  open: boolean;
  handleClose: () => void;
  attributeName: string;
  commodityId: number;
  onAdd: (commodityId: number, name: string) => Promise<{ id: number; name: string }>;
};

export default function AddCommodityAttributeModal({
  open,
  handleClose,
  attributeName,
  commodityId,
  onAdd,
}: AddCommodityAttributeModalProps) {
  const formik = useFormik({
    initialValues: { name: '' },
    validateOnChange: false,
    onSubmit: async (input, helpers) => {
      try {
        await onAdd(commodityId, input.name);
        helpers.resetForm();
        handleClose();
      } catch (error) {
        console.error(`Error creating ${attributeName.toLowerCase()}:`, error);
        notifyUnlessSurfaced(error, `Failed to create ${attributeName.toLowerCase()}.`);
      }
    },
  });

  const values = formik.values;

  const handleCloseWithReset = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <AgModal open={open} onClose={handleCloseWithReset} title={<>Enter New {attributeName} Name</>}>
      <form onSubmit={formik.handleSubmit}>
        <AgModalContent>
          <p className="text-sm text-neutral-secondary mb-3">
            This {attributeName.toLowerCase()} will be added to the selected commodity.
          </p>
          <AgInput
            autoFocus
            label="Name"
            value={values.name}
            onChange={(value) => formik.setFieldValue('name', value)}
            className="pt-3"
          />
        </AgModalContent>
        <AgModalActions>
          <AgButton variant="primary" type="submit" isDisabled={values.name?.length === 0}>
            Add
          </AgButton>
        </AgModalActions>
      </form>
    </AgModal>
  );
}
